import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, ChevronDown, Trophy } from "lucide-react";

export default function HomePage() {
  const [sports, setSports] = useState([]);
  // Seed from the pre-boot cache so the hero uses the correct brand colors on
  // first render — otherwise the inline styles briefly show fallback #0A1128/#FACC15.
  const [settings, setSettings] = useState(() => (typeof window !== "undefined" && window.__siteSettings) || {});
  useEffect(() => {
    api.get("/sports").then(r => setSports(r.data));
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const totalRecords = sports.reduce((n, s) => n + (s.record_count || 0), 0);
  const sportCount = sports.length;
  const fmt = new Intl.NumberFormat().format;
  const showStats = sportCount > 0;

  const primary = settings.primary_color || "#0A1128";
  const accent = settings.accent_color || "#FACC15";
  const heroName = "Florida High School Athletic Association Record Book";
  const heroTagline = settings.tagline || "Every school. Every season. Every record.";
  // Admin-provided landing background (upload OR URL). Falls back to dark gradient below.
  const landingBg = (settings.landing_bg_url || "").trim();

  const scrollToSports = (e) => {
    e.preventDefault();
    const el = document.getElementById("sports-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden text-white"
        data-testid="hero-section"
        style={{
          background: `radial-gradient(1200px 600px at 20% 10%, ${accent}22 0%, transparent 55%),
                       radial-gradient(1000px 500px at 85% 15%, ${primary}55 0%, transparent 60%),
                       radial-gradient(800px 400px at 50% 100%, ${primary}77 0%, transparent 60%),
                       linear-gradient(180deg, #05070d 0%, #0a0e1c 50%, #05070d 100%)`,
        }}
      >
        {/* Admin-defined landing background — falls back to the dark gradient above when unset */}
        {landingBg && (
          <>
            <img
              src={landingBg}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover opacity-55 pointer-events-none"
              data-testid="hero-landing-bg"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/70 via-black/60 to-black/80" aria-hidden/>
          </>
        )}

        {/* Blurred hero image (if provided) as extra depth */}
        {settings.hero_image_url && (
          <img
            src={settings.hero_image_url}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-[0.08] blur-2xl scale-110 pointer-events-none"
            data-testid="hero-bg-image"
          />
        )}

        {/* Turf line pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          aria-hidden
          style={{
            backgroundImage: `
              repeating-linear-gradient(90deg, transparent 0 78px, ${accent}88 78px 79px),
              repeating-linear-gradient(0deg, transparent 0 78px, ${accent}44 78px 79px)`,
          }}
        />

        {/* Stadium light rays */}
        <div
          className="absolute inset-x-0 top-0 h-[70%] pointer-events-none opacity-40 mix-blend-screen"
          aria-hidden
          style={{
            background: `
              conic-gradient(from 200deg at 15% -10%, transparent 0deg, ${accent}33 12deg, transparent 24deg),
              conic-gradient(from 160deg at 85% -10%, transparent 0deg, #ffffff22 12deg, transparent 24deg)`,
            filter: "blur(40px)",
          }}
        />

        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden
             style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)" }}/>

        {/* Grain noise */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.09] mix-blend-overlay" aria-hidden
             style={{
               backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
             }}
        />

        {/* Content */}
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 flex flex-col items-center text-center">
          {/* Association overline */}
          <p className="font-overline text-[10px] md:text-xs tracking-[0.35em] mb-6 animate-hero-fade"
             style={{ color: accent, animationDelay: "0ms" }}
             data-testid="hero-overline">
            {settings.conference ? `${settings.conference} · ` : ""}{settings.season || "Official Record Book"}
          </p>

          {/* Logo */}
          <div className="mb-8 md:mb-10 animate-hero-fade" style={{ animationDelay: "80ms" }}>
            {settings.logo_url ? (
              <div className="relative inline-block">
                <div className="absolute inset-0 blur-2xl opacity-40 scale-110"
                     style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 65%)` }} aria-hidden/>
                <img
                  src={settings.logo_url}
                  alt={heroName}
                  className="relative h-28 md:h-36 lg:h-40 w-auto object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
                  data-testid="hero-logo"
                />
              </div>
            ) : (
              <div className="relative inline-flex items-center justify-center w-28 h-28 md:w-36 md:h-36"
                   style={{ background: accent }} data-testid="hero-logo-fallback">
                <Trophy className="w-14 h-14 md:w-20 md:h-20" style={{ color: primary }} strokeWidth={2}/>
              </div>
            )}
          </div>

          {/* Association name */}
          <h1
            className="font-stat text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight uppercase animate-hero-fade text-white max-w-5xl mx-auto"
            style={{
              animationDelay: "160ms",
              textShadow: `0 2px 40px rgba(0,0,0,0.8), 0 0 60px ${primary}66`,
            }}
            data-testid="hero-title"
          >
            {heroName}
          </h1>

          {/* Accent underline */}
          <div className="mt-6 h-1 w-24 md:w-32 animate-hero-fade" style={{ background: accent, animationDelay: "220ms" }} aria-hidden/>

          {/* Tagline */}
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 animate-hero-fade"
             style={{ animationDelay: "280ms" }}
             data-testid="hero-tagline">
            {heroTagline}
          </p>

          {/* CTA */}
          <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center gap-4 animate-hero-fade"
               style={{ animationDelay: "360ms" }}>
            <a
              href="#sports-grid"
              onClick={scrollToSports}
              data-testid="hero-cta-view-records"
              className="group relative inline-flex items-center gap-3 px-8 py-4 md:px-10 md:py-5 font-heading tracking-[0.15em] text-sm md:text-base uppercase text-white overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              style={{
                background: primary,
                boxShadow: `0 20px 40px -12px ${primary}CC, 0 0 0 1px ${accent}55 inset`,
              }}
            >
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(90deg, ${primary}, ${accent}33, ${primary})` }} aria-hidden/>
              <span className="relative">View All Records</span>
              <ChevronRight className="relative w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2.5}/>
            </a>
            <Link
              to="/search"
              data-testid="hero-cta-search"
              className="inline-flex items-center gap-2 px-6 py-4 font-heading tracking-[0.15em] text-sm uppercase border-2 border-white/25 text-white/90 hover:border-white hover:text-white transition-colors"
            >
              Search Records
            </Link>
          </div>

          {/* Stats strip */}
          {showStats && (
            <div className="mt-14 md:mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 animate-hero-fade"
                 style={{ animationDelay: "440ms" }} data-testid="record-counter">
              <div className="flex flex-col items-center">
                <span className="font-stat text-4xl md:text-5xl leading-none" style={{ color: accent }} data-testid="counter-records">
                  {fmt(totalRecords)}
                </span>
                <span className="font-overline text-[10px] text-white/60 mt-2">Official Records</span>
              </div>
              <div className="w-px h-10 bg-white/15" aria-hidden/>
              <div className="flex flex-col items-center">
                <span className="font-stat text-4xl md:text-5xl leading-none text-white" data-testid="counter-sports">
                  {sportCount}
                </span>
                <span className="font-overline text-[10px] text-white/60 mt-2">{sportCount === 1 ? "Sport" : "Sports"}</span>
              </div>
              {settings.season && (
                <>
                  <div className="w-px h-10 bg-white/15" aria-hidden/>
                  <div className="flex flex-col items-center">
                    <span className="font-stat text-4xl md:text-5xl leading-none text-white">
                      {(settings.season.match(/\d{4}/) || ["Live"])[0]}
                    </span>
                    <span className="font-overline text-[10px] text-white/60 mt-2">Updated</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Scroll hint */}
          <a href="#sports-grid" onClick={scrollToSports} aria-label="Scroll to sports"
             className="mt-16 md:mt-20 inline-flex flex-col items-center text-white/50 hover:text-white transition-colors"
             data-testid="hero-scroll-hint">
            <span className="font-overline text-[9px] tracking-[0.3em] mb-1">Explore</span>
            <ChevronDown className="w-5 h-5 animate-bounce-slow"/>
          </a>
        </div>
      </section>

      {/* SPORTS GRID */}
      <section id="sports-grid" className="max-w-7xl mx-auto px-6 py-14 md:py-20 scroll-mt-8">
        <div className="mb-8 md:mb-10 flex items-end justify-between gap-4 border-b-2 pb-4" style={{ borderColor: primary }}>
          <div>
            <p className="font-overline text-[10px] mb-1" style={{ color: primary }}>Browse</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight" style={{ color: primary }}>All Sports</h2>
          </div>
          <p className="text-sm text-slate-500 hidden md:block">Select a sport to explore its record book.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sports-grid">
          {sports.map(s => {
            const cardPrimary = s.primary_color || primary;
            const cardAccent = s.accent_color || accent;
            return (
              <Link key={s.id} to={`/sports/${s.slug}`} data-testid={`sport-card-${s.slug}`}
                className="group relative block h-64 overflow-hidden border-2 hover-lift-hard bg-slate-900"
                style={{ borderColor: cardPrimary }}>
                {s.image_url && (
                  <img src={s.image_url} alt={s.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-70 transition-opacity"
                    onError={e => { e.target.style.display = 'none'; }}/>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20"/>
                <div className="relative h-full flex flex-col justify-between p-5 text-white">
                  <div className="flex justify-between items-start">
                    <span className="font-overline text-[10px] px-2 py-1" style={{ background: cardAccent, color: cardPrimary }}>
                      {s.record_count} {s.record_count === 1 ? "Record" : "Records"}
                    </span>
                    <ChevronRight className="w-5 h-5 opacity-80 group-hover:translate-x-1 transition-transform"/>
                  </div>
                  <div>
                    {s.logo_url && <img src={s.logo_url} alt="" className="h-8 mb-2 object-contain"/>}
                    <h3 className="font-heading text-4xl leading-none tracking-tight">{s.name.toUpperCase()}</h3>
                    {(s.tagline || s.description) && <p className="text-sm text-white/80 mt-2 line-clamp-2">{s.tagline || s.description}</p>}
                    {s.season && <p className="text-[10px] mt-2 font-overline" style={{ color: cardAccent }}>{s.season}</p>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
